import React, { useEffect, useRef } from "react";
import "./PaymentResultModal.css";

// Presentation-only result modal. It renders whatever status/message it is
// given and calls onClose when dismissed — it holds no payment logic itself.
// Drive it from a real payment result (e.g. the outcome of a Stripe charge).

const DEFAULTS = {
  success: {
    title: "Success",
    message: "Your payment was completed successfully.",
  },
  error: {
    title: "Ooopss",
    message: "Transaction unsuccessful, Payment merchant issue",
  },
};

const SuccessIcon = () => (
  <svg viewBox="0 0 52 52" className="prm-icon-svg" aria-hidden="true">
    <circle className="prm-icon-circle" cx="26" cy="26" r="24" />
    <path className="prm-icon-mark" d="M15 27 l7 7 l15 -15" />
  </svg>
);

const ErrorIcon = () => (
  <svg viewBox="0 0 52 52" className="prm-icon-svg" aria-hidden="true">
    <circle className="prm-icon-circle" cx="26" cy="26" r="24" />
    <path className="prm-icon-mark" d="M18 18 L34 34 M34 18 L18 34" />
  </svg>
);

const PaymentResultModal = ({
  open,
  status = "error",
  title,
  message,
  buttonLabel = "OK",
  onClose,
}) => {
  const buttonRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);

    // Lock background scroll while the modal is visible.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Move focus to the primary action for keyboard / screen-reader users.
    buttonRef.current?.focus();

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const variant = status === "success" ? "success" : "error";
  const copy = DEFAULTS[variant];

  return (
    <div className="prm-overlay" role="presentation" onClick={onClose}>
      <div
        className={`prm-dialog prm-${variant}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="prm-title"
        aria-describedby="prm-message"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="prm-icon">
          {variant === "success" ? <SuccessIcon /> : <ErrorIcon />}
        </div>
        <h3 id="prm-title" className="prm-title">
          {title ?? copy.title}
        </h3>
        <p id="prm-message" className="prm-message">
          {message ?? copy.message}
        </p>
        <button
          type="button"
          ref={buttonRef}
          className="prm-button"
          onClick={onClose}
        >
          {buttonLabel}
        </button>
      </div>
    </div>
  );
};

export default PaymentResultModal;
